import { PaperAirplaneIcon, PaperClipIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import debug from 'debug';
import React, { useRef, useState } from 'react';
import { Constants } from '../../../../Constants';
import { useChat, useFile, useIsChatLocked } from '../../../../libs/hooks';
import { IAppConfigResult, useAppConfig } from '../../../../libs/hooks/useAppConfig';
import { useAuthoritive } from '../../../../libs/hooks/useAuthoritive';
import { GetResponseOptions } from '../../../../libs/hooks/useChat';
import { AlertType } from '../../../../libs/models/AlertType';
import { DocumentUploadState } from '../../../../libs/models/ChatMemorySource';
import { ChatMessageType } from '../../../../libs/models/ChatMessage';
import { ISiteInfo } from '../../../../libs/models/SelectedSites';
import { IAskResult } from '../../../../libs/semantic-kernel/model/AskResult';
import { IAppConfigValue } from '../../../../libs/services/AppConfigService';
import { ClientSpeechService } from '../../../../libs/services/ClientSpeechService';
import { useAppDispatch, useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import { addAlert, setNotifications } from '../../../../redux/features/app/appSlice';
import { ChatState } from '../../../../redux/features/conversations/ChatState';
import {
    editConversationAuthoritiveDocuments,
    editConversationInput,
    editConversationSiteSource,
    hideIntelligentFilteringMessage,
    setChatMemorySources,
    updateBotResponseStatus,
    updateUserIsTyping,
} from '../../../../redux/features/conversations/conversationsSlice';
import { BorderBeam } from '../../../Effects/BorderBeam';
import { ShineBorder } from '../../../Effects/ShineBorder';
import { Icons } from '../../../Icons/Icons';
import { NOTIFICATION_TEMPLATES } from '../Notifications/NotificationConstants';
import { BucketType } from '../../../../redux/features/buckets/BucketsState';
import { BucketPopoverContent } from './Buckets/BucketPopoverContent';
import { BucketsToolbar } from './Buckets/BucketToolbar';
import { ChatStatus } from './ChatStatus';
import { EffectivePromptsDialog } from './EffectivePromptsDialog/EffectivePromptsDialog';

const log = debug(Constants.debug.root).extend('chat-input');
interface ChatInputProps {
    isDraggingOver?: boolean;
    onDragLeave: React.DragEventHandler<HTMLDivElement | HTMLTextAreaElement>;
    onSubmit: (options: GetResponseOptions) => Promise<IAskResult | null>;
}

export const ChatInput: React.FC<ChatInputProps> = ({ isDraggingOver, onDragLeave, onSubmit }) => {
    const [open, setOpen] = useState(false);
    const [readyForInput, setReadyForInput] = useState(true);
    const [isRecordingSpeech, setIsRecordingSpeech] = useState(false);
    const [value, setValue] = useState('');
    const [inputPlaceholder] = useState('Ask me a question...');
    const [showBucketMenu, setShowBucketMenu] = React.useState<boolean>(false);
    const [isBucketLoaded, setIsBucketLoaded] = React.useState<boolean>(false);
    const chat = useChat();
    const dispatch = useAppDispatch();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { Buckets } = useAppSelector((state: RootState) => state.buckets);
    const [disclaimer, setDisclaimer] = useState<string>();
    const [importingDocuments, setImportingDocuments] = React.useState(false);
    const [importDocumentCount, setImportDocumentCount] = React.useState(0);
    const { chatMemorySources } = conversations[selectedId];
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const emailAddress = activeUserInfo?.email ?? '';
    const authoritive = useAuthoritive();
    const user = emailAddress;
    const chatState: ChatState = conversations[selectedId];
    const appConfig = useAppConfig();
    const fileHandler = useFile();
    const { isLockedChat } = useIsChatLocked(conversations, selectedId);
    const documentFileRef = useRef<HTMLInputElement | null>(null);
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
    const bucketMenuRef = useRef<HTMLDivElement>(null);
    const [bucketMenuPos, setBucketMenuPos] = useState(196);
    const [selectedSites, setSelectedSites] = useState<ISiteInfo[]>([]);
    const [isRemovingSelectedSite, setIsRemovingSelectedSite] = useState<string>('');

    const clientSpeechService = new ClientSpeechService();
    const speechRecogniser = clientSpeechService.getSpeechRecogniser(setValue, setIsRecordingSpeech);

    let chatScope: string | undefined = undefined;

    if (selectedId && conversations[selectedId]?.chatScope) {
        chatScope = conversations[selectedId].chatScope;
    }

    const getDisclaimer = async () => {
        const defaultDisclaimer = `
            ${import.meta.env.VITE_APP_NAME} is a language model that replies like a confident friend, but like a confident friend it
            can make mistakes, so it isn't a bad idea to double check if you find yourself asking
            something you aren't very knowledgable on.
        `;

        const response: IAppConfigResult<IAppConfigValue> = await appConfig.getConfigSingleValue('Disclaimer', user);

        if (response.Success) {
            setDisclaimer(response.Data?.Value ?? defaultDisclaimer);
        } else {
            setDisclaimer(defaultDisclaimer);
        }
    };

    const handleSubmit = (value: string, messageType: ChatMessageType = ChatMessageType.Message) => {
        if (importingDocuments) {
            const notification = NOTIFICATION_TEMPLATES.PREVENT_SEND_AS_UPLOADING;
            dispatch(setNotifications([notification]));
            return;
        }

        if (textAreaRef.current) {
            textAreaRef.current.style.height = 'auto';
            setBucketMenuPos(196); // Reset to default position
        }

        if (value.trim() === '') {
            return; // only submit if value is not empty
        }

        dispatch(hideIntelligentFilteringMessage({ chatId: selectedId }));

        setValue('');

        dispatch(editConversationInput({ id: selectedId, newInput: '' }));
        dispatch(updateBotResponseStatus({ chatId: selectedId, status: 'Calling the kernel' }));

        onSubmit({ value, messageType, chatId: selectedId }).catch((error) => {
            const message = `Error submitting chat input: ${(error as Error).message}`;
            log(message);
            dispatch(
                addAlert({
                    type: AlertType.Error,
                    message,
                }),
            );
        });
    };

    const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
        onDragLeave(e);

        void fileHandler
            .handleImport(selectedId, documentFileRef, false, undefined, e.dataTransfer.files)
            .finally(() => {
                void chat.getChatMemorySources(selectedId).then((sources) => {
                    dispatch(setChatMemorySources({ chatId: selectedId, chatMemorySources: sources }));
                });
            });
    };

    React.useEffect(() => {
        getDisclaimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (chatMemorySources && chatMemorySources.length > 0) {
            const inProgressCount = chatMemorySources.filter(
                (x) => x.documentUploadState === DocumentUploadState.InProgress,
            ).length;

            if (inProgressCount > 0) {
                const batchSize = Math.max(importDocumentCount, inProgressCount);
                if (inProgressCount > importDocumentCount) {
                    setImportDocumentCount(inProgressCount);
                }

                setImportingDocuments(true);
                dispatch(
                    updateBotResponseStatus({
                        chatId: selectedId,
                        status: batchSize > 1 ? `Uploading documents` : `Uploading document`,
                    }),
                );
            } else {
                if (importDocumentCount > 0) {
                    dispatch(
                        setNotifications([
                            {
                                ...NOTIFICATION_TEMPLATES.FILE_UPLOAD_COMPLETE,
                                description: NOTIFICATION_TEMPLATES.FILE_UPLOAD_COMPLETE.description.replace(
                                    '[s]',
                                    importDocumentCount > 1 ? 's' : '',
                                ),
                            },
                        ]),
                    );
                }

                setImportingDocuments(false);
                dispatch(updateBotResponseStatus({ chatId: selectedId, status: undefined }));
                setImportDocumentCount(0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatMemorySources, selectedId, importDocumentCount]);

    React.useEffect(() => {
        if (isBucketLoaded) return;

        const getAuthDocsScopeName = async (user: string) => {
            await authoritive.getAuthDocsScopeName(user).then((scopeName) => {
                if (scopeName) {
                    setShowBucketMenu(true);
                    setIsBucketLoaded(true);
                }
            });
        };
        if (activeUserInfo) {
            getAuthDocsScopeName(activeUserInfo.email).catch((error) => {
                log('An error occurred while getting auth docs scope name', error);
            });
        }

        // we want to close the bucket menu when the user clicks outside of it
        window.addEventListener('click', () => {
            setShowBucketMenu(false);
        });
    }, [activeUserInfo, authoritive, isBucketLoaded]);

    React.useEffect(() => {
        if (bucketMenuRef.current) {
            bucketMenuRef.current.focus();
            const bucketYOffset = textAreaRef?.current ? textAreaRef.current.scrollHeight : 50;

            setBucketMenuPos(146 + bucketYOffset);
        }
    }, [showBucketMenu]);

    React.useEffect(() => {
        if (
            conversations[selectedId].botResponseStatus &&
            conversations[selectedId].botResponseStatus.length > 0 &&
            conversations[selectedId].botResponseStatus.indexOf('Uploading') === -1
        ) {
            setReadyForInput(false);
        } else {
            setReadyForInput(true);
        }

        if (chatState.selectedSites && chatState.selectedSites.length > 0) {
            setSelectedSites(chatState.selectedSites);
        } else {
            setSelectedSites([]);
        }
    }, [conversations, selectedId]);

    React.useEffect(() => {
        if (!chatState.selectedSites || chatState.selectedSites.length === 0) return;
        const currentSelectedSites = chatState.selectedSites;
        setSelectedSites(currentSelectedSites);
        setIsRemovingSelectedSite('');
    }, [chatState.selectedSites]);

    const handleBucketMenuByKey = () => {
        if (textAreaRef.current) {
            setShowBucketMenu(true);
        }
    };

    const handleSpeechToggle = () => {
        if (isRecordingSpeech) {
            speechRecogniser.stop();
        } else {
            speechRecogniser.start();
        }
    };

    const updateConversationAuthoritiveDocuments = async (updatedDocuments: string[], usedBuckets: string[]) => {
        if (JSON.stringify(chatState.authoritiveDocuments) === JSON.stringify(updatedDocuments)) return;

        if (chatState.isSaved) {
            await chat.editChat(
                selectedId,
                chatState.title,
                chatState.memoryBalance,
                chatState.chatScope,
                chatState.selectedSites,
                chatState.hidden,
                updatedDocuments,
                usedBuckets
            );
        }
        dispatch(
            editConversationAuthoritiveDocuments({
                id: selectedId,
                newDocuments: updatedDocuments,
                usedBuckets
            }),
        );
    };

    React.useEffect(() => {
        if (!chatState.chatScope?.startsWith('AuthDocs:') || chatState.intelligentFilteringBucketId === '') {
            return;
        }
        if (Buckets.length) {
            const selectedSources = Buckets.filter((b) => b.selected);
            const usedBuckets = [...new Set(selectedSources.filter(b => b.type == "BucketSource").map(s => s.parent?.id).filter(id => id !== undefined))];
            const authoritiveDocuments: string[] = [];
            if (selectedSources.length) {
                const bucketSources = selectedSources.filter((b) => b.type === BucketType.BucketSource);
                authoritiveDocuments.push(...bucketSources.map((b) => b.id));
                const bucketGroups = selectedSources.filter((b) => b.type === BucketType.Bucket);
                bucketGroups.forEach((bucketGroup) => {
                    const childItems = selectedSources.filter((item) => item.parent?.id === bucketGroup.id);
                    authoritiveDocuments.push(...childItems.map((item) => item.id));
                });
            }
            updateConversationAuthoritiveDocuments(authoritiveDocuments, usedBuckets).catch((error) => {
                log('An error occured whilst editing chat state', error);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        Buckets,
        chatState.id,
        chatState.chatScope,
        chatState.authoritiveDocuments,
        chatState.intelligentFilteringBucketId,
    ]);

    const onRemoveSelectedSite = (selectedSiteId: string) => {
        setIsRemovingSelectedSite(selectedSiteId);
        const updatedSelectedSites: ISiteInfo[] = selectedSites.filter((site) => site.siteId !== selectedSiteId);
        updateConversationSiteSource(updatedSelectedSites).catch((error) => {
            log('An error occured whilst editing chat state', error);
        });

        // setSelectedSites(updatedSelectedSites);
    };

    const updateConversationSiteSource = async (updatedSelectedSites: ISiteInfo[]) => {
        if (chatState.isSaved) {
            await chat.editChat(
                selectedId,
                chatState.title,
                chatState.memoryBalance,
                chatState.chatScope,
                updatedSelectedSites,
                chatState.hidden,
                chatState.authoritiveDocuments,
            );
        }

        dispatch(
            editConversationSiteSource({
                id: selectedId,
                siteSources: updatedSelectedSites,
            }),
        );
    };

    return (
        <div className="relative" data-popover-target="true">
            {chatState.chatScope?.startsWith('AuthDocs:') && showBucketMenu && (
                <div
                    ref={bucketMenuRef}
                    hidden={!showBucketMenu}
                    className="absolute z-5 transition-all w-4/5 md:w-1/2 sm:min-w-xs ml-2 dark:bg-primary-800 rounded-full shadow dark:text-slate-300 sm:text-base sm:leading-7 flex flex-row"
                    style={{ bottom: `${bucketMenuPos}px` }}>
                    <ShineBorder className="text-center w-full flex" color={'#E44EE9'} borderWidth={2}>
                        <BucketPopoverContent chatId={selectedId} />
                    </ShineBorder>
                    <div />
                </div>
            )}
            <form className="w-full items-center border-t border-slate-300 bg-slate-300 p-2 dark:border-slate-700 dark:bg-slate-800 mr-4">
                <label htmlFor="chat-input" className="sr-only">
                    Enter your prompt
                </label>
                <div className="max-h-7 text-slate-500">
                    <ChatStatus />
                </div>
                {chatState.chatScope?.startsWith('AuthDocs:') && !isLockedChat && (
                    <BucketsToolbar
                        chatId={selectedId}
                        isShowBucketMenu={showBucketMenu}
                        onShowBucketMenu={() => {
                            setShowBucketMenu(!showBucketMenu);
                        }}
                    />
                )}
                {chatScope?.startsWith('SPO:') &&
                    chatScope.split(':').length === 2 &&
                    chatState &&
                    selectedSites &&
                    selectedSites.length > 0 && (
                        <div className="flex flex-row mt-1 mb-2 items-center">
                            <span className="text-sm ml-1 mr-2 font-medium">{`Selected source${selectedSites.length > 1 ? 's' : ''}:`}</span>
                            <div className="flex">
                                <div className="flex flex-wrap items-center flex-row">
                                    {selectedSites.map((site) => (
                                        <div
                                            key={`site-${site.siteId}`}
                                            className="min-h-9 items-center mr-1 mb-1 bg-primary-300 text-slate-50 dark:bg-primary-400 dark:text-slate-300 rounded-md pl-3 dark:text-primary flex">
                                            <div className="flex mr-2 min-w-5 size-5 items-center justify-center">
                                                <Icons.sharepoint />
                                            </div>
                                            <div className="text-md pr-2">{site.siteName}</div>
                                            {!isLockedChat && (
                                                <div
                                                    className="cursor-pointer hover:text-red-500"
                                                    onClick={() => {
                                                        onRemoveSelectedSite(site.siteId);
                                                    }}>
                                                    {isRemovingSelectedSite !== site.siteId ? (
                                                        <XCircleIcon
                                                            className="text-slate-100 dark:text-slate-200 mr-2 h-5 w-5 hover:text-lavender-dark dark:hover:text-white transition-all"
                                                            strokeWidth="2"
                                                        />
                                                    ) : (
                                                        <svg
                                                            aria-hidden="true"
                                                            className="inline w-5 h-5 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-primary-300"
                                                            viewBox="0 0 100 101"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg">
                                                            <path
                                                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                                fill="currentColor"
                                                            />
                                                            <path
                                                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                                fill="currentFill"
                                                            />
                                                        </svg>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                <div className="relative rounded-lg overflow-hidden flex flex-row flex-nowrap">
                    {(isRecordingSpeech || isDraggingOver) && <BorderBeam duration={10} anchor={90} />}
                    {window.SpeechRecognition || window.webkitSpeechRecognition ? (
                        <button
                            type="button"
                            disabled={conversations[selectedId].disabled || isLockedChat}
                            onClick={handleSpeechToggle}
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${isLockedChat ? 'text-slate-400 hover:text-slate-400 dark:text-slate-500 dark:hover:text-slate-500' : 'text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-600'}`}>
                            <svg
                                aria-hidden="true"
                                className={`h-5 w-5 ${isRecordingSpeech ? 'text-primary-600' : ''}`}
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                                strokeWidth="2"
                                stroke="currentColor"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <path d="M9 2m0 3a3 3 0 0 1 3 -3h0a3 3 0 0 1 3 3v5a3 3 0 0 1 -3 3h0a3 3 0 0 1 -3 -3z"></path>
                                <path d="M5 10a7 7 0 0 0 14 0"></path>
                                <path d="M8 21l8 0"></path>
                                <path d="M12 17l0 4"></path>
                            </svg>
                            <span className="sr-only">Use voice input</span>
                        </button>
                    ) : undefined}
                    <textarea
                        id="chat-input"
                        title="Chat input"
                        aria-label="Chat input field. Click enter to submit input."
                        ref={textAreaRef}
                        onDrop={handleDrop}
                        className={`block w-full resize-none rounded-xl border-none ${isLockedChat ? 'bg-slate-200' : 'bg-slate-50'} p-4 pl-10 pr-20 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-600 dark:bg-slate-900 dark:text-slate-200 dark:placeholder-slate-400 dark:focus:ring-blue-600 focus:ring-inset ${!readyForInput ? 'opacity-50' : ''}`}
                        placeholder={!isLockedChat ? inputPlaceholder : ''}
                        disabled={
                            (conversations[selectedId].disabled || isLockedChat || !readyForInput) &&
                            !importingDocuments
                        }
                        value={isDraggingOver ? 'Drop your files here to upload them to the chat' : value}
                        rows={1}
                        onChange={(event) => {
                            if (isDraggingOver) {
                                return;
                            } else {
                                const textarea = event.target;
                                textarea.style.height = 'auto';
                                textarea.style.height =
                                    textarea.scrollHeight < 200 ? `${textarea.scrollHeight}px` : '200px';

                                setBucketMenuPos(146 + textarea.scrollHeight);
                            }

                            setValue(event.target.value);
                            dispatch(editConversationInput({ id: selectedId, newInput: event.target.value }));
                        }}
                        onFocus={() => {
                            // update the locally stored value to the current value
                            const chatInput = document.getElementById('chat-input');

                            if (chatInput) {
                                setValue((chatInput as HTMLTextAreaElement).value);
                            }
                            // User is considered typing if the input is in focus
                            if (activeUserInfo) {
                                dispatch(
                                    updateUserIsTyping({
                                        userId: activeUserInfo.id,
                                        chatId: selectedId,
                                        isTyping: true,
                                    }),
                                );
                            }
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                handleSubmit(value);
                            }
                            if (event.key === '/') {
                                handleBucketMenuByKey();
                            }
                            if (event.key === '^' && (window.SpeechRecognition || window.webkitSpeechRecognition)) {
                                handleSpeechToggle();
                            }
                        }}
                        onBlur={() => {
                            // User is considered not typing if the input is not  in focus
                            if (activeUserInfo) {
                                dispatch(
                                    updateUserIsTyping({
                                        userId: activeUserInfo.id,
                                        chatId: selectedId,
                                        isTyping: false,
                                    }),
                                );
                            }
                            if (isRecordingSpeech) {
                                speechRecogniser.stop();
                            }
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => handleSubmit(value)}
                        disabled={
                            (conversations[selectedId].disabled || isLockedChat || !readyForInput) &&
                            !importingDocuments
                        }
                        className={`absolute inset-y-2 right-2 rounded-lg shadow-md ${isLockedChat ? 'bg-slate-300' : 'bg-primary-300'} px-4 py-1 text-sm font-medium text-slate-200 ${isLockedChat || !readyForInput || importingDocuments ? 'hover:bg-slate-300' : 'hover:bg-primary-700'} focus:outline-none focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-800 sm:text-base transition-all duration-300 ${!readyForInput ? 'opacity-50' : ''}`}>
                        <PaperAirplaneIcon className={`h-6 w-6`} strokeWidth={1.5} />
                        <span className="sr-only">Send message</span>
                    </button>
                    {/*</ShineBorder>*/}
                </div>
                <div className={`${isDraggingOver ? 'opacity-75 blur-xs' : ''} flex w-full py-2 items-center`}>
                    {/* Hidden input for file upload. Only accept .txt and .pdf files for now. */}
                    <input
                        type="file"
                        ref={documentFileRef}
                        style={{ display: 'none' }}
                        accept={Constants.app.importTypes}
                        multiple={true}
                        onChange={() => {
                            void fileHandler.handleImport(selectedId, documentFileRef, false).finally(() => {
                                void chat.getChatMemorySources(selectedId).then((sources) => {
                                    dispatch(setChatMemorySources({ chatId: selectedId, chatMemorySources: sources }));
                                });
                            });
                        }}
                    />
                    <button
                        disabled={
                            conversations[selectedId].disabled || importingDocuments || isLockedChat || !readyForInput
                        }
                        onClick={() => documentFileRef.current?.click()}
                        type="button"
                        className={`${conversations[selectedId].disabled || importingDocuments || isLockedChat ? 'inline-flex justify-center rounded-lg p-2 dark:text-slate-400 cursor-not-allowed opacity-50' : 'inline-flex cursor-pointer justify-center rounded-lg p-2 text-slate-500 dark:text-slate-400 dark:hover:bg-slate-600 dark:hover:text-slate-50 transition-all duration-200'} ${!readyForInput ? 'opacity-50' : 'hover:bg-slate-50 hover:text-slate-900'}`}>
                        <span className="sr-only">Attach file</span>
                        <PaperClipIcon className="h-5 w-5" strokeWidth={1.5} />
                        <span className="px-2 text-sm">Upload a document</span>
                    </button>
                    {importingDocuments && (
                        <div
                            className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-primary-600 border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
                            role="status"></div>
                    )}
                </div>{' '}
                <div
                    className={`${isDraggingOver ? 'opacity-75 blur-xs' : ''} flex w-full items-center text-xs text-slate-500 dark:text-slate-400`}>
                    <div className="mx-2 flex min-h-full w-full flex-col text-center">
                        <p>{disclaimer}</p>
                        { import.meta.env.VITE_APP_BUG_REPORT_URL && import.meta.env.VITE_APP_BUG_REPORT_URL.startsWith('http') && (  
                            <p>
                                <a href={import.meta.env.VITE_APP_BUG_REPORT_URL} 
                                target='_blank' 
                                className="font-bold hover:text-slate-900 dark:hover:text-slate-100">
                                    [Report a bug]
                                </a>
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="inline-flex cursor-pointer justify-center rounded-lg p-1 text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-600 dark:hover:text-slate-50 transition-all duration-200">
                        <span className="sr-only">Show prompt guidance</span>
                        <QuestionMarkCircleIcon className="h-6 w-6" strokeWidth={2} />
                    </button>
                </div>
            </form>
            <EffectivePromptsDialog open={open} setOpen={setOpen} />
        </div>
    );
};
